"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary } from "@/features/dashboard/hooks";
import { type Course, getLocalizedField } from "../api";
import type { Locale } from "@/lib/i18n";
import { useCoursePricing } from "./CoursePricingProvider";
import { SprintCountdown } from "./SprintCountdown";
import { useCourseEligibilityStatus } from "../use-course-eligibility-status";
import { useCoursePlanPricingUiModel } from "../hooks/use-course-plan-pricing-ui-model";
import {
  normalizePricingPlanId,
  resolvePricingUiOwnedState,
} from "../lib/resolve-course-plan-pricing-ui-state";
import { CourseShowcaseSectionHeader } from "./detail/CourseShowcaseSectionHeader";
import { RefundLineIcon } from "./detail/RefundLineIcon";
import { CoursePricingTierPlanCard } from "./CoursePricingTierPlanCard";
import { CoursePricingOwnedStatusBanner } from "./CoursePricingOwnedStatusBanner";
import {
  getSprintPlanIdForMulti,
  hasFutureSprint,
  isMostPopularHeuristic,
} from "../lib/course-pricing-helpers";

type Props = {
  courseId: string;
  slug: string;
  eligibility?: Course["eligibility"];
  locale: Locale;
  firstLessonId: string | null;
  /** Localized refund / guarantee copy from API; footer hidden when empty. */
  refundPolicyNote?: string | null;
  /**
   * When the sidebar pricing block is hidden, show the plan-ownership banner
   * here so plan-aware owners still see ownership copy above the grid.
   */
  showStandalonePlanOwnershipBanner?: boolean;
};

export function CourseMultiPlanPricingSection({
  courseId,
  slug,
  eligibility,
  locale,
  firstLessonId,
  refundPolicyNote,
  showStandalonePlanOwnershipBanner = false,
}: Props) {
  const t = useTranslations("courseDetail");
  const {
    plans,
    selectedPlanId,
    recommendedPlan,
    setSelectedPlanId,
    mode,
  } = useCoursePricing();
  const { isAuthenticated, getToken } = useAuth();
  const token = getToken();
  const hasEligibilityGate = !!eligibility;
  const { data: eligibilityStatus } = useCourseEligibilityStatus(
    { slug, eligibility },
    isAuthenticated && hasEligibilityGate,
  );
  const eligibilityLocked =
    hasEligibilityGate &&
    (!isAuthenticated || eligibilityStatus?.passed !== true);
  const eligibilityLockedReason = t("eligibilityTierLocked");
  const { data: library } = useLibrary(token ?? undefined);
  const ownedCourse = library?.courses.find((c) => c.courseId === courseId);
  const isOwned = !!ownedCourse;
  const pricingUiModel = useCoursePlanPricingUiModel(
    ownedCourse,
    isAuthenticated,
    plans,
  );

  const [showAllPlans, setShowAllPlans] = useState(false);

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.sortOrder - b.sortOrder),
    [plans],
  );

  useEffect(() => {
    if (mode !== "multi") return;
    if (!isAuthenticated || !isOwned || pricingUiModel.isLegacyEnrollmentWithoutPlanId)
      return;
    const id = ownedCourse?.pricingPlanId;
    if (!id) return;
    if (
      !sortedPlans.some(
        (p) => normalizePricingPlanId(p._id) === normalizePricingPlanId(id),
      )
    ) {
      return;
    }
    setSelectedPlanId((prev) => (prev === null ? id : prev));
  }, [
    mode,
    isAuthenticated,
    isOwned,
    pricingUiModel.isLegacyEnrollmentWithoutPlanId,
    ownedCourse?.pricingPlanId,
    setSelectedPlanId,
    sortedPlans,
  ]);

  const sprintPlanId = useMemo(
    () => getSprintPlanIdForMulti(sortedPlans),
    [sortedPlans],
  );
  const sectionSprintEndsAt = sortedPlans.find((p) => p._id === sprintPlanId)?.sprintEndsAt;
  const sectionShowsSprint =
    !!sectionSprintEndsAt && hasFutureSprint(sectionSprintEndsAt);

  const visiblePlanCards = useMemo(() => {
    if (sortedPlans.length <= 3 || showAllPlans) return sortedPlans;
    return sortedPlans.slice(0, 3);
  }, [sortedPlans, showAllPlans]);

  const bannerPlanName =
    pricingUiModel.effectiveOwnedPlanForCopy &&
    (getLocalizedField(
      pricingUiModel.effectiveOwnedPlanForCopy.name_en,
      pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
      locale,
    ) ??
      pricingUiModel.effectiveOwnedPlanForCopy.name_en);

  if (mode !== "multi") {
    return null;
  }

  const manyVisible = visiblePlanCards.length > 3;

  return (
    <section
      id="course-pricing-comparison"
      className="scroll-mt-28"
      aria-labelledby="course-pricing-stream-heading"
    >
      <div className="mb-10 max-w-3xl">
        <CourseShowcaseSectionHeader
          titleId="course-pricing-stream-heading"
          eyebrow={t("showcase.pricingEyebrow")}
          title={t("pricingTiersTitle")}
          description={t("pricingTiersDescription")}
        />
        {sectionShowsSprint && sectionSprintEndsAt && (
          <SprintCountdown
            endsAt={sectionSprintEndsAt}
            label={t("sprintCountdown.label")}
            size="full"
            className="mt-6 max-w-md"
          />
        )}
      </div>

      {showStandalonePlanOwnershipBanner && isAuthenticated && isOwned ? (
        <CoursePricingOwnedStatusBanner
          locale={locale}
          className="mb-8 max-w-3xl"
          planDisplayName={bannerPlanName || t("planOwnership.yourPlanShort")}
          hasActiveUpgradeTarget={pricingUiModel.hasActiveUpgradeTarget}
        />
      ) : null}

      <div
        className={cn(
          "grid auto-rows-fr gap-6",
          manyVisible
            ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
        )}
        role="list"
      >
        {visiblePlanCards.map((plan) => {
          const ownedState = resolvePricingUiOwnedState(plan, pricingUiModel, {
            isAuthenticated,
            isOwned,
          });
          return (
            <div key={plan._id} role="listitem" className="min-w-0 flex h-full">
              <CoursePricingTierPlanCard
                plan={plan}
                locale={locale}
                ownedState={ownedState}
                isPopular={isMostPopularHeuristic(plan)}
                recommendedPlanId={recommendedPlan?._id}
                courseId={courseId}
                slug={slug}
                firstLessonId={firstLessonId}
                sortedPlans={sortedPlans}
                isAuthenticated={isAuthenticated}
                isOwned={isOwned}
                pricingUiModel={pricingUiModel}
                ownedCourse={ownedCourse}
                eligibilityLocked={eligibilityLocked}
                eligibilityLockedReason={eligibilityLockedReason}
                showSprintCountdown={
                  !sectionShowsSprint && plan._id === sprintPlanId && !!plan.sprintEndsAt
                }
                isSelected={selectedPlanId === plan._id}
                onSelectPlan={setSelectedPlanId}
              />
            </div>
          );
        })}
      </div>

      {sortedPlans.length > 3 ? (
        <div className="mt-8 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            className="min-h-11 rounded-[10px] border-neutral-300 px-6 font-semibold"
            onClick={() => setShowAllPlans((o) => !o)}
          >
            {showAllPlans ? t("showFewerPlans") : t("viewAllPlans")}
          </Button>
        </div>
      ) : null}

      {refundPolicyNote ? (
        <div
          className="mt-10 flex justify-center border-t border-neutral-200/80 pt-8"
          role="note"
        >
          <div className="flex max-w-2xl items-start gap-2.5 text-[13px] leading-relaxed text-neutral-600 sm:text-sm">
            <RefundLineIcon />
            <p className="min-w-0 text-pretty">{refundPolicyNote}</p>
          </div>
        </div>
      ) : null}
    </section>
  );
}
